import {RootLayout, RsnButton, RsnDialog, RsnSelect, useBoolean} from '@raison/ui-kit';
import {useAtom} from 'jotai';
import {AtomWithQueryResult} from 'jotai-tanstack-query';
import {Atom} from 'jotai/index';
import React, {useId, useState} from 'react';
import {SuspenseAndErrorBoundary} from '~/entities/fallbacks';
import {useReportsFormsRequests} from '../api/hooks/useReportsFormsRequests';
import AccountStatementReportForm from './AccountStatementReportForm';
import {ReportsPageSkeleton} from './ReportsPageSkeleton';
import {SuccessDialog} from './SuccessDialog';
import TransactionsReportForm from './TransactionsReportForm';
import {getIcon} from './utils/geticon';

export type ReportType = 'transactions' | 'account';

interface Props {
    emailAtom: Atom<AtomWithQueryResult<string>>;
}

function ReportsPageContent({emailAtom}: Props) {
    const formId = useId();
    const [reportType, setReportType] = useState<ReportType>('transactions');
    const [email] = useAtom(emailAtom);

    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useBoolean(false);

    const {transactions, account} = useReportsFormsRequests(setIsSuccessDialogOpen.on);
    const isButtonDisabled = reportType === 'transactions' ? transactions.isPending : account.isPending;

    return (
        <RootLayout title='Reports'>
            <div className='flex flex-1 flex-col'>
                <div className='flex flex-1 flex-col gap-6 py-4'>
                    <div className='px-4'>
                        <RsnSelect<ReportType>
                            label={<RsnSelect.Label>Document type</RsnSelect.Label>}
                            options={reportTypes}
                            value={reportType}
                            onChange={setReportType}
                        />
                    </div>
                    {reportType === 'transactions' ? (
                        <TransactionsReportForm formId={formId} {...transactions} />
                    ) : (
                        <AccountStatementReportForm formId={formId} {...account} />
                    )}
                </div>
                <div className='flex flex-col gap-4 p-4'>
                    {!!email.data && (
                        <div className='text fv-text-l-regular text-center text-shade-60'>
                            The report will be generated and sent to{' '}
                            <span className='whitespace-nowrap'>{maskEmail(email.data)}</span>.
                        </div>
                    )}
                    <RsnButton.Primary type='submit' form={formId} disabled={isButtonDisabled}>
                        Send to my email
                    </RsnButton.Primary>
                </div>
            </div>
            <RsnDialog.Controller onClose={setIsSuccessDialogOpen.off}>
                {isSuccessDialogOpen && <SuccessDialog email={email.data ? maskEmail(email.data) : undefined} />}
            </RsnDialog.Controller>
        </RootLayout>
    );
}

export function ReportsPage({emailAtom}: Props) {
    return (
        <SuspenseAndErrorBoundary skeleton={<ReportsPageSkeleton />}>
            <ReportsPageContent emailAtom={emailAtom} />
        </SuspenseAndErrorBoundary>
    );
}

export function maskEmail(email: string) {
    return email.slice(0, Math.min(email.indexOf('@'), 2)) + '***' + email.slice(email.indexOf('@'));
}

const reportTypes: RsnSelect.Props<ReportType>['options'] = [
    {
        label: 'Transactions report',
        value: 'transactions',
        key: 'transitions',
        icon: getIcon('local://transfer-file-line'),
    },
    {
        label: 'Account statement report',
        value: 'account',
        key: 'statement',
        icon: getIcon('local://file-chart-line'),
    },
];
